import { Box, CircularProgress, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import ComputerIcon from '@mui/icons-material/Computer';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HistoryIcon from '@mui/icons-material/History';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityEndpointDetailsHref } from 'src/mindtime/config/securityNav';
import type { Column, TableData } from 'src/types/table';
import { useSecurityDashboardGrid } from './shared/useSecurityDashboardGrid';
import { useWazuhSubOrgOverviewData } from './shared/useWazuhOverviewData';
import {
    SecurityDashboardCard,
    SecuritySecurityScoreCard,
} from './shared/securityDashboardCard';
import { SecurityDashboardHeader } from './shared/securityDashboardToolbar';
import {
    formatImpactPercent,
    formatRelativeTime
} from './shared/wazuhDashboardUtils';
import { EdrDateTime } from './shared/edrPrimitives';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const COLOR_BLUE = '#3182CE';

const mutedCellStyle: React.CSSProperties = {
    color: '#718096',
    fontStyle: 'italic',
};

// ─────────────────────────────────────────────────────────────────────────────
// Local types — no external type imports added
// ─────────────────────────────────────────────────────────────────────────────

type Kpi = {
    id: string;
    label: string;
    value: string;
    /** Optional colour applied to the big value number. */
    valueColor?: string;
    /** Plain subtitle text shown below the value */
    subtitle: string;
    /** Optional coloured trend text shown below the value (e.g. "+03 this month") */
    subtitleColored?: string;
    subtitleColor?: string;
    icon: ReactNode;
};

const COLOR_TEXT_DEFAULT = '#1A202C';
const COLOR_SUCCESS = '#15803D';
const COLOR_WARNING = '#DD6B20';
const COLOR_DANGER = '#DC2626';

/** One row in the "Recommended Actions" table */
type RecommendedActionRow = {
    id: number;
    action: string;
    priority: string;
    affected: string;
};

/** One row in the "Endpoints Requiring Immediate Action" table */
type EndpointRow = {
    id: string;
    endpoint: string;
    issue: string;
    severity: string;
    lastSeen: string;
};

/** One row in the "Recent Critical Incidents" table */
type IncidentRow = {
    id: number;
    incident: string;
    endpoint: string;
    time: string;
};

/** One row in the "Most Vulnerable Endpoints" table */
type VulnerableEndpointRow = {
    id: string;
    endpoint: string;
    vulnerabilities: number;
    severity: string;
};

/** One item in the "Recent Activity" feed */
type ActivityItem = {
    id: number;
    user: string;
    action: string;
    detail: string;
    time: string;
    dotColor: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Severity pill
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_PILL_STYLES: Record<string, { color: string; bg: string }> = {
    critical: { color: '#DC2626', bg: '#FEE2E2' },
    high: { color: '#DD6B20', bg: '#FEEBC8' },
    medium: { color: '#B45309', bg: '#FEF3C7' },
    low: { color: '#15803D', bg: '#DCFCE7' },
};

const getSeverityPillStyle = (severity: string) =>
    SEVERITY_PILL_STYLES[severity?.toLowerCase()] ?? SEVERITY_PILL_STYLES.critical;

const SeverityPill: FC<{ label: string }> = ({ label }) => {
    const { color, bg } = getSeverityPillStyle(label);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 10,
                bgcolor: bg,
                fontSize: 11,
                fontWeight: 600,
                color,
                textTransform: 'capitalize',
            }}
        >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
            {label}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Layout helpers
// ─────────────────────────────────────────────────────────────────────────────

const AlignRight: FC<{ children: ReactNode }> = ({ children }) => (
    <Box style={{ textAlign: 'right' }}>{children}</Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Generic paginated table card
// ─────────────────────────────────────────────────────────────────────────────

const ListTableCard: FC<{
    icon: ReactNode;
    title: string;
    columns: Column[];
    rows: TableData[];
    showing: string;
    style?: React.CSSProperties;
    onRowClick?: (row: TableData) => void;
}> = ({ icon, title, columns, rows, showing, style, onRowClick }) => (
    <SecurityDashboardCard
        title={title}
        icon={icon}
        overflow="hidden"
        style={style}
        footerAlign="space-between"
        footer={(
            <>
                <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}>
                    {showing}
                </Typography>
                <Box display="flex" style={{ gap: 16 }}>
                    <Typography style={{ fontSize: 11, fontWeight: 600, color: '#A0AEC0', cursor: 'pointer' }}>Previous</Typography>
                    <Typography style={{ fontSize: 11, fontWeight: 600, color: COLOR_BLUE, cursor: 'pointer' }}>Next</Typography>
                </Box>
            </>
        )}
    >
        <Table
            columns={columns}
            data={rows}
            showPagination={false}
            onRowClick={onRowClick}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
        />
    </SecurityDashboardCard>
);

// ─────────────────────────────────────────────────────────────────────────────
// Column definitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recommended Actions: Action | Priority | Affected
 */
const RECOMMENDED_ACTION_COLUMNS: Column[] = [
    {
        id: 'action',
        label: 'Action',
        render: (row) => (
            <span style={{ fontWeight: 600 }}>
                {(row as unknown as RecommendedActionRow).action}
            </span>
        ),
    },
    {
        id: 'priority',
        label: 'Priority',
        render: (row) => (
            <SeverityPill label={(row as unknown as RecommendedActionRow).priority} />
        ),
    },
    {
        id: 'affected',
        label: 'Affected',
        align: 'right',
        render: (row) => (
            <AlignRight>
                <span style={mutedCellStyle}>
                    {(row as unknown as RecommendedActionRow).affected}
                </span>
            </AlignRight>
        ),
    },
];

/**
 * Endpoints Requiring Immediate Action: Endpoint | Issue | Severity | Last Seen
 * (no Organisation column — matches the image)
 */
const ENDPOINT_COLUMNS: Column[] = [
    {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => (
            <span style={{ fontWeight: 600 }}>
                {(row as unknown as EndpointRow).endpoint}
            </span>
        ),
    },
    {
        id: 'issue',
        label: 'Issue',
        render: (row) => (
            <span style={mutedCellStyle}>{(row as unknown as EndpointRow).issue}</span>
        ),
    },
    {
        id: 'severity',
        label: 'Severity',
        render: (row) => (
            <SeverityPill label={(row as unknown as EndpointRow).severity} />
        ),
    },
    {
        id: 'lastSeen',
        label: 'Last Seen',
        align: 'right',
        render: (row) => (
            <AlignRight>
                <EdrDateTime value={(row as unknown as EndpointRow).lastSeen} />
            </AlignRight>
        ),
    },
];

/**
 * Recent Critical Incidents: Incident | Endpoint | Time
 * (no Client column — matches the image)
 */
const INCIDENT_COLUMNS: Column[] = [
    {
        id: 'incident',
        label: 'Incident',
        render: (row) => (
            <span style={{ fontWeight: 600 }}>
                {(row as unknown as IncidentRow).incident}
            </span>
        ),
    },
    {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => (
            <span style={mutedCellStyle}>{(row as unknown as IncidentRow).endpoint}</span>
        ),
    },
    {
        id: 'time',
        label: 'Time',
        align: 'right',
        render: (row) => (
            <AlignRight>
                <EdrDateTime value={(row as unknown as IncidentRow).time} />
            </AlignRight>
        ),
    },
];

/**
 * Most Vulnerable Endpoints: Endpoint | Vulnerabilities | Severity
 */
const VULNERABLE_ENDPOINT_COLUMNS: Column[] = [
    {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => (
            <span style={{ fontWeight: 600 }}>
                {(row as unknown as VulnerableEndpointRow).endpoint}
            </span>
        ),
    },
    {
        id: 'vulnerabilities',
        label: 'Vulnerabilities',
        render: (row) => (
            <span style={mutedCellStyle}>
                {String((row as unknown as VulnerableEndpointRow).vulnerabilities).padStart(2, '0')}
            </span>
        ),
    },
    {
        id: 'severity',
        label: 'Severity',
        align: 'right',
        render: (row) => (
            <AlignRight>
                <SeverityPill label={(row as unknown as VulnerableEndpointRow).severity} />
            </AlignRight>
        ),
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// KPI item
// ─────────────────────────────────────────────────────────────────────────────

const KpiItem: FC<{ kpi: Kpi; loading: boolean; showDivider: boolean }> = ({
    kpi,
    loading,
    showDivider,
}) => (
    <Box
        style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '0 20px',
            borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
        }}
    >
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
            <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}>
                {kpi.label}
            </Typography>
            {kpi.icon}
        </Box>

        {loading ? (
            <CircularProgress size={22} />
        ) : (
            <Typography style={{ fontSize: 26, fontWeight: 700, color: kpi.valueColor ?? COLOR_TEXT_DEFAULT, lineHeight: 1.1 }}>
                {kpi.value}
            </Typography>
        )}

        {/* Coloured trend line (e.g. "↑ 03 this month") */}
        {kpi.subtitleColored && (
            <Typography style={{ fontSize: 11, color: kpi.subtitleColor ?? '#38A169', marginTop: 4, fontWeight: 600 }}>
                {kpi.subtitleColored}
            </Typography>
        )}

        {/* Plain subtitle line (e.g. "Today: 03") */}
        {kpi.subtitle && (
            <Typography style={{ fontSize: 11, color: '#718096', marginTop: kpi.subtitleColored ? 2 : 4 }}>
                {kpi.subtitle}
            </Typography>
        )}
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Agent status segmented bar (Endpoint Health card)
// ─────────────────────────────────────────────────────────────────────────────

const AgentStatusBar: FC<{
    healthy: number;
    warning: number;
    critical: number;
}> = ({ healthy, warning, critical }) => {
    const total = healthy + warning + critical || 1; // avoid /0
    const hPct = (healthy / total) * 100;
    const wPct = (warning / total) * 100;
    const cPct = (critical / total) * 100;

    return (
        <Box>
            {/* Three stat columns */}
            <Box display="flex" style={{ marginBottom: 10 }}>
                <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase', marginBottom: 2 }}>
                        Healthy
                    </Typography>
                    <Typography style={{ fontSize: 22, fontWeight: 700, color: '#15803D' }}>
                        {healthy.toLocaleString()}
                    </Typography>
                </Box>
                <Box style={{ flex: 1, textAlign: 'center' }}>
                    <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase', marginBottom: 2 }}>
                        Warning
                    </Typography>
                    <Typography style={{ fontSize: 22, fontWeight: 700, color: '#DD6B20' }}>
                        {warning.toLocaleString()}
                    </Typography>
                </Box>
                <Box style={{ flex: 1, textAlign: 'right' }}>
                    <Typography style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase', marginBottom: 2 }}>
                        Critical
                    </Typography>
                    <Typography style={{ fontSize: 22, fontWeight: 700, color: '#DC2626' }}>
                        {critical.toLocaleString()}
                    </Typography>
                </Box>
            </Box>

            {/* Segmented progress bar */}
            <Box style={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', gap: 2 }}>
                <Box style={{ flex: hPct, backgroundColor: '#15803D', minWidth: hPct > 0 ? 4 : 0 }} />
                <Box style={{ flex: wPct, backgroundColor: '#DD6B20', minWidth: wPct > 0 ? 4 : 0 }} />
                <Box style={{ flex: cPct, backgroundColor: '#DC2626', minWidth: cPct > 0 ? 4 : 0 }} />
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Recent Activity feed
// ─────────────────────────────────────────────────────────────────────────────

const ActivityFeed: FC<{ items: ActivityItem[] }> = ({ items }) => (
    <Box display="flex" flexDirection="column">
        {items.map((item, index) => (
            <Box
                key={item.id}
                display="flex"
                alignItems="flex-start"
                style={{
                    padding: '10px 16px',
                    borderBottom: index < items.length - 1 ? '1px solid #F7FAFC' : 'none',
                    gap: 10,
                }}
            >
                {/* Coloured dot */}
                <Box
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: item.dotColor,
                        marginTop: 3,
                        flexShrink: 0,
                    }}
                />
                {/* Text content */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1A202C' }}>
                            {item.user}
                        </Typography>
                        <Typography style={{ fontSize: 11, color: '#A0AEC0', flexShrink: 0, marginLeft: 8 }}>
                            {item.time}
                        </Typography>
                    </Box>
                    <Typography style={{ fontSize: 11, color: '#718096' }}>
                        {item.action}
                    </Typography>
                    <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>
                        {item.detail}
                    </Typography>
                </Box>
            </Box>
        ))}
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Grid style helpers
// ─────────────────────────────────────────────────────────────────────────────

const getTableGridStyle = (
    isStacked: boolean,
    column: number,
    row: number,
): React.CSSProperties =>
    isStacked ? { minWidth: 0 } : { minWidth: 0, gridColumn: column, gridRow: row };

const getRightSidebarGridStyle = (
    isStacked: boolean,
    isTablet: boolean,
): React.CSSProperties => {
    if (isStacked) {
        return { minWidth: 0 };
    }
    if (isTablet) {
        return { minWidth: 0, gridColumn: '1 / -1', gridRow: 3 };
    }
    return { minWidth: 0, gridColumn: 3, gridRow: '1 / 3' };
};

// ─────────────────────────────────────────────────────────────────────────────
// Page component
// ─────────────────────────────────────────────────────────────────────────────

const SubOrgDashboard: FC = () => {
    const navigate = useNavigate();
    // const { getOrganizationName } = useWazuhScope();
    const overviewData = useWazuhSubOrgOverviewData();
    const { overviewMainColumns, isOverviewTablet, isOverviewStacked } = useSecurityDashboardGrid();

    const overviewPortfolioRowColumns =
        isOverviewStacked || isOverviewTablet ? 'minmax(0, 1fr)' : overviewMainColumns;

    const [searchQuery, setSearchQuery] = useState('');

    const {
        loading: isOverviewLoading,
        portfolioHealth,
        agentOverview,
        securityScore,
        endpointsActionRequired,
        recentIncidents,
        mostVulnerableEndpoints,
    } = overviewData;

    const recommendedActions = null;
    const recentActivity = null;

    const agentStatus = agentOverview?.agentStatus;

    // ── Recommended Actions ───────────────────────────────────────────────────
    const [recommendedActionRows, setRecommendedActionRows] = useState<RecommendedActionRow[]>([]);

    useEffect(() => {
        const items: Array<{
            action?: string;
            label?: string;
            priority?: string;
            severity?: string;
            affected?: string;
            affectedLabel?: string;
        }> = recommendedActions?.items ?? [];

        setRecommendedActionRows(
            items.map((item, index) => ({
                id: index + 1,
                action: item.action ?? item.label ?? '—',
                priority: item.priority ?? item.severity ?? 'Critical',
                affected: item.affected ?? item.affectedLabel ?? '—',
            }))
        );
    }, [recommendedActions]);

    // ── Endpoints Requiring Immediate Action ──────────────────────────────────
    const [endpointRows, setEndpointRows] = useState<EndpointRow[]>([]);

    useEffect(() => {
        const items: Array<{
            systemWazuhAgentId?: string;
            endpoint?: string;
            name?: string;
            issue?: string;
            issueLabel?: string;
            severity?: string;
            mainIssueSeverity?: string;
            lastSeen?: string;
        }> = endpointsActionRequired?.items ?? [];

        let cancelled = false;

        if (!items.length) {
            setEndpointRows([]);
        } else {
            const rows: EndpointRow[] = items.map((item) => ({
                id: item.systemWazuhAgentId?.trim() ?? '',
                endpoint: item.endpoint ?? item.name ?? '—',
                issue: item.issue ?? item.issueLabel ?? '—',
                severity: item.severity ?? item.mainIssueSeverity ?? 'Critical',
                lastSeen: item.lastSeen ?? '—',
            }));
            if (!cancelled) setEndpointRows(rows);
        }

        return () => { cancelled = true; };
    }, [endpointsActionRequired]);

    // ── Recent Critical Incidents ─────────────────────────────────────────────
    const [incidentRows, setIncidentRows] = useState<IncidentRow[]>([]);

    useEffect(() => {
        const items: Array<{
            incident?: string;
            endpoint?: string;
            time?: string;
            organizationId?: string;
            client?: string;
        }> = recentIncidents?.items ?? [];

        let cancelled = false;

        if (!items.length) {
            setIncidentRows([]);
        } else {
            const rows: IncidentRow[] = items.map((item, index) => ({
                id: index + 1,
                incident: item.incident ?? '—',
                endpoint: item.endpoint ?? '—',
                time: item.time ?? '—',
            }));
            if (!cancelled) setIncidentRows(rows);
        }

        return () => { cancelled = true; };
    }, [recentIncidents]);

    // ── Most Vulnerable Endpoints ─────────────────────────────────────────────
    const [vulnerableEndpointRows, setVulnerableEndpointRows] = useState<VulnerableEndpointRow[]>([]);

    useEffect(() => {
        const items: Array<{
            systemWazuhAgentId?: string;
            endpoint?: string;
            name?: string;
            vulnerabilities?: number;
            vulnerabilityCount?: number;
            severity?: string;
            mainIssueSeverity?: string;
        }> = mostVulnerableEndpoints?.items ?? [];

        setVulnerableEndpointRows(
            items.map((item) => ({
                id: item.systemWazuhAgentId?.trim() ?? '',
                endpoint: item.endpoint ?? item.name ?? '—',
                vulnerabilities: item.vulnerabilities ?? item.vulnerabilityCount ?? 0,
                severity: item.severity ?? item.mainIssueSeverity ?? 'Critical',
            }))
        );
    }, [mostVulnerableEndpoints]);

    // ── Recent Activity feed ──────────────────────────────────────────────────
    const activityItems = useMemo<ActivityItem[]>(() => {
        const items: Array<{
            user?: string;
            actor?: string;
            action?: string;
            eventType?: string;
            detail?: string;
            endpoint?: string;
            resource?: string;
            time?: string;
            dotColor?: string;
        }> = recentActivity?.items ?? [];

        return items.map((item, index) => ({
            id: index + 1,
            user: item.user ?? item.actor ?? '—',
            action: item.action ?? item.eventType ?? '—',
            detail: item.detail ?? item.endpoint ?? item.resource ?? '—',
            time: item.time ? formatRelativeTime(item.time) : '—',
            dotColor: item.dotColor ?? COLOR_BLUE,
        }));
    }, [recentActivity]);

    // ── Security score impacts ────────────────────────────────────────────────
    const impactRows = useMemo(
        () =>
            (securityScore?.impacts ?? []).map(
                (impact: { factor: string; impactPercent: number }, index: number) => ({
                    id: `impact-${index}`,
                    label: `${impact.factor}:`,
                    impact: formatImpactPercent(impact.impactPercent),
                })
            ),
        [securityScore]
    );

    const connectedClients = portfolioHealth?.connectedClients;
    const activeThreats = portfolioHealth?.activeThreats;
    const openVulnerabilities = portfolioHealth?.openVulnerabilities;
    const backupStatus = portfolioHealth?.backupStatus;

    const formatBackupSubtitle = (lastBackup: string | null | undefined) => {
        if (!lastBackup) return 'Not available';
        return `Last backup: ${formatRelativeTime(lastBackup)}`;
    };

    const backupStatusLabel = backupStatus?.status ?? '—';
    const backupStatusLower = String(backupStatusLabel).toLowerCase();
    const backupIsHealthy = backupStatusLower === 'healthy';
    const backupIsWarning = backupStatusLower === 'warning' || backupStatusLower === 'stale';
    const backupIsFailed = backupStatusLower === 'failed' || backupStatusLower === 'critical' || backupStatusLower === 'unknown';
    const backupValueColor = backupIsHealthy
        ? COLOR_SUCCESS
        : backupIsFailed
            ? COLOR_DANGER
            : backupIsWarning
                ? COLOR_WARNING
                : undefined;

    const activeThreatsTotal = activeThreats?.total ?? 0;
    const openVulnerabilitiesTotal = openVulnerabilities?.total ?? 0;
    const activeThreatsIncrease = activeThreats?.percentageIncrease ?? 0;
    const openVulnIncrease = openVulnerabilities?.percentageIncrease ?? 0;

    const kpis: Kpi[] = [
        {
            id: 'connected_clients',
            label: 'Connected Endpoints',
            value: connectedClients !== undefined
                ? connectedClients.total.toLocaleString()
                : '—',
            subtitle: '',
            subtitleColored: connectedClients !== undefined
                ? `↑ ${connectedClients.thisMonth.toString().padStart(2, '0')} this month`
                : undefined,
            subtitleColor: COLOR_SUCCESS,
            icon: <PeopleOutlineIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'active_threats',
            label: 'Active Threats',
            value: activeThreatsTotal.toLocaleString(),
            valueColor: activeThreatsTotal > 0 ? COLOR_WARNING : undefined,
            subtitle: `Today: ${(activeThreats?.today ?? 0).toString().padStart(2, '0')}`,
            subtitleColored:
                activeThreatsIncrease > 0
                    ? `↑ ${activeThreatsIncrease.toString().padStart(2, '0')}%`
                    : activeThreatsIncrease < 0
                        ? `↓ ${Math.abs(activeThreatsIncrease).toString().padStart(2, '0')}%`
                        : undefined,
            subtitleColor: activeThreatsIncrease > 0 ? COLOR_DANGER : COLOR_SUCCESS,
            icon: <ComputerIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'open_vulnerabilities',
            label: 'Open Vulnerabilities',
            value: openVulnerabilitiesTotal.toLocaleString(),
            valueColor: openVulnerabilitiesTotal > 0 ? COLOR_DANGER : undefined,
            subtitle: `Today: ${openVulnerabilities?.today ?? 0}`,
            subtitleColored:
                openVulnIncrease > 0
                    ? `↑ ${openVulnIncrease.toString().padStart(2, '0')}%`
                    : openVulnIncrease < 0
                        ? `↓ ${Math.abs(openVulnIncrease).toString().padStart(2, '0')}%`
                        : undefined,
            subtitleColor: openVulnIncrease > 0 ? COLOR_DANGER : COLOR_SUCCESS,
            icon: <ReportProblemIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'backup_status',
            label: 'Backup Status',
            value: backupStatusLabel,
            valueColor: backupValueColor,
            subtitle: formatBackupSubtitle(backupStatus?.lastBackup),
            subtitleColored: undefined,
            subtitleColor: undefined,
            icon: <AssignmentIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
    ];

    // ── Agent health numbers ───────────────────────────────────────────────────
    const agentHealthy = agentStatus?.active ?? 0;
    const agentWarning = agentStatus?.warning ?? 0;
    const agentCritical = agentStatus?.offline ?? 0;

    // ── Showing strings ────────────────────────────────────────────────────────
    const recommendedShowing = recommendedActions
        ? `Showing ${recommendedActionRows.length} of ${recommendedActions.totalRecords}`
        : 'Showing 0 of 0';
    const endpointShowing = endpointsActionRequired
        ? `Showing ${endpointRows.length} of ${endpointsActionRequired.totalRecords}`
        : 'Showing 0 of 0';
    const incidentShowing = recentIncidents
        ? `Showing ${incidentRows.length} of ${recentIncidents.totalRecords}`
        : 'Showing 0 of 0';
    const vulnerableShowing = mostVulnerableEndpoints
        ? `Showing ${vulnerableEndpointRows.length} of ${mostVulnerableEndpoints.totalRecords}`
        : 'Showing 0 of 0';

    const handleEndpointRowClick = (row: TableData): void => {
        const { id: systemWazuhAgentId, endpoint } = row as unknown as EndpointRow;
        if (!systemWazuhAgentId) return;
        navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
            state: { endpointName: endpoint },
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <Helmet>
                <title>{getPageTitle('Security — Dashboard')}</title>
            </Helmet>

            <Box
                p={3}
                display="flex"
                flexDirection="column"
                style={{ minHeight: '100%', boxSizing: 'border-box', gap: 16 }}
            >
                {/* ── Top toolbar ── */}
                <SecurityDashboardHeader
                    searchValue={searchQuery}
                    onSearchChange={setSearchQuery}
                />

                {/* ── Row 1: Portfolio Health + Security Score ── */}
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: overviewPortfolioRowColumns,
                        gap: 16,
                        alignItems: 'stretch',
                        minWidth: 0,
                    }}
                >
                    {/* Portfolio Health (spans first 2 columns when not stacked) */}
                    <SecurityDashboardCard
                        title="Portfolio Health"
                        icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}
                        headerRight={
                            <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>
                                Updated: Just now
                            </Typography>
                        }
                        style={{
                            minWidth: 0,
                            height: '100%',
                            ...(isOverviewStacked || isOverviewTablet ? {} : { gridColumn: '1 / 3' }),
                        }}
                    >
                        <Box display="flex" sx={{ py: 2 }}>
                            {kpis.map((kpi, index) => (
                                <KpiItem
                                    key={kpi.id}
                                    kpi={kpi}
                                    loading={isOverviewLoading}
                                    showDivider={index > 0}
                                />
                            ))}
                        </Box>
                    </SecurityDashboardCard>

                    {/* Security Score (col 3) */}
                    <SecuritySecurityScoreCard
                        icon={<ShowChartIcon style={{ fontSize: 18 }} />}
                        score={securityScore ? `${securityScore.score}%` : '—'}
                        comparison={
                            securityScore
                                ? `${securityScore.vsAverage >= 0 ? '+' : ''}${securityScore.vsAverage} vs avg`
                                : '—'
                        }
                        impactRows={impactRows}
                        style={{
                            height: '100%',
                            minWidth: 0,
                            ...(isOverviewStacked || isOverviewTablet ? {} : { gridColumn: 3 }),
                        }}
                    />
                </Box>

                {/* ── Main grid: 2 table cols + 1 sidebar col ── */}
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: overviewMainColumns,
                        gap: 16,
                        alignItems: 'stretch',
                        minWidth: 0,
                    }}
                >
                    {/* Col 1 Row 1 — Recommended Actions */}
                    <ListTableCard
                        icon={<WarningIcon style={{ fontSize: 18 }} />}
                        title="Recommended Actions"
                        columns={RECOMMENDED_ACTION_COLUMNS}
                        rows={recommendedActionRows as unknown as TableData[]}
                        showing={recommendedShowing}
                        style={getTableGridStyle(isOverviewStacked, 1, 1)}
                    />

                    {/* Col 2 Row 1 — Endpoints Requiring Immediate Action */}
                    <ListTableCard
                        icon={<ErrorOutlineIcon style={{ fontSize: 18 }} />}
                        title="Endpoints Requiring Immediate Action"
                        columns={ENDPOINT_COLUMNS}
                        rows={endpointRows as unknown as TableData[]}
                        showing={endpointShowing}
                        style={getTableGridStyle(isOverviewStacked, 2, 1)}
                        onRowClick={handleEndpointRowClick}
                    />

                    {/* Col 1 Row 2 — Recent Critical Incidents */}
                    <ListTableCard
                        icon={<WhatshotIcon style={{ fontSize: 18 }} />}
                        title="Recent Critical Incidents"
                        columns={INCIDENT_COLUMNS}
                        rows={incidentRows as unknown as TableData[]}
                        showing={incidentShowing}
                        style={getTableGridStyle(isOverviewStacked, 1, 2)}
                    />

                    {/* Col 2 Row 2 — Most Vulnerable Endpoints */}
                    <ListTableCard
                        icon={<BugReportIcon style={{ fontSize: 18 }} />}
                        title="Most Vulnerable Endpoints"
                        columns={VULNERABLE_ENDPOINT_COLUMNS}
                        rows={vulnerableEndpointRows as unknown as TableData[]}
                        showing={vulnerableShowing}
                        style={getTableGridStyle(isOverviewStacked, 2, 2)}
                        onRowClick={handleEndpointRowClick}
                    />

                    {/* Col 3 Row 1-2 — Endpoint Health + Recent Activity stacked */}
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            ...getRightSidebarGridStyle(isOverviewStacked, isOverviewTablet),
                        }}
                    >
                        {/* Endpoint Health */}
                        <SecurityDashboardCard
                            title="Endpoint Health"
                            icon={<ComputerIcon style={{ fontSize: 18 }} />}
                        >
                            <Box style={{ padding: '12px 16px 16px' }}>
                                <Typography
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        letterSpacing: '0.4px',
                                        color: '#A0AEC0',
                                        textTransform: 'uppercase',
                                        marginBottom: 10,
                                    }}
                                >
                                    Agent Status
                                </Typography>
                                {isOverviewLoading ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    <AgentStatusBar
                                        healthy={agentHealthy}
                                        warning={agentWarning}
                                        critical={agentCritical}
                                    />
                                )}
                            </Box>
                        </SecurityDashboardCard>

                        {/* Recent Activity */}
                        <SecurityDashboardCard
                            title="Recent Activity"
                            icon={<HistoryIcon style={{ fontSize: 18 }} />}
                            headerRight={
                                <TuneIcon style={{ fontSize: 16, color: '#A0AEC0', cursor: 'pointer' }} />
                            }
                            style={{ flex: 1 }}
                            footer={(
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 4, cursor: 'pointer' }}
                                >
                                    <ImportExportIcon style={{ fontSize: 14, color: COLOR_BLUE }} />
                                    <Typography
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: '0.3px',
                                            color: COLOR_BLUE,
                                        }}
                                    >
                                        VIEW FULL AUDIT LOG
                                    </Typography>
                                </Box>
                            )}
                        >
                            {activityItems.length === 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{ flex: 1, minHeight: 200, padding: 24 }}
                                >
                                    <Typography
                                        style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#94A3B8' }}
                                    >
                                        Not available via API
                                    </Typography>
                                </Box>
                            ) : (
                                <ActivityFeed items={activityItems} />
                            )}
                        </SecurityDashboardCard>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default SubOrgDashboard;